"use client";

import React, { useState } from "react";
import { getBookLogo, getBookCode, getBookDisplayName } from "@/lib/whale-watch/whale-watch-shared";
import { cn } from "@/lib/utils";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { NOT_BETTABLE_CLASS, notBettableIn, notHere } from "@/lib/book-location";

export type WhaleWatchBookLogoSize = "xs" | "sm" | "md" | "lg";

export interface WhaleWatchBookLogoProps {
  book: string;
  size?: WhaleWatchBookLogoSize;
  className?: string;
  title?: string;
}

export const BOOK_LOGO_CLASS = "bg-zinc-800 font-semibold";

const SIZE_MAP: Record<WhaleWatchBookLogoSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
};

const RADIUS_MAP: Record<WhaleWatchBookLogoSize, string> = {
  xs: "rounded-sm",
  sm: "rounded",
  md: "rounded-lg",
  lg: "rounded-lg",
};

export function WhaleWatchBookLogo({
  book,
  size = "sm",
  className,
  title,
}: WhaleWatchBookLogoProps) {
  const px = SIZE_MAP[size];
  const src = getBookLogo(book);
  const code = getBookCode(book);
  const [imgError, setImgError] = useState(false);

  const displayName = book.charAt(0).toUpperCase() + book.slice(1).toLowerCase();

  return (
    <div
      className={cn(
        RADIUS_MAP[size],
        "overflow-hidden bg-zinc-700/80 font-bold shrink-0 flex items-center justify-center cursor-default",
        className
      )}
      style={{ width: px, height: px }}
      title={title ?? displayName}
    >
      {!imgError ? (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          src={src}
          alt={title ?? book}
          style={{ width: px - 2, height: px - 2, objectFit: "contain" }}
          onError={() => setImgError(true)}
        />
      ) : (
        <span
          className="text-zinc-400"
          style={{ fontSize: px * 0.35 }}
        >
          {code.slice(0, 2)}
        </span>
      )}
    </div>
  );
}


const DEFAULT_MAX_VISIBLE = 3;

interface StackedBookLogosProps {
  bestBook: string;
  secondaryBooks: string[];
  maxVisible?: number;
}

export const StackedBookLogos = React.memo(function StackedBookLogos({
  bestBook,
  secondaryBooks,
  maxVisible = DEFAULT_MAX_VISIBLE,
}: StackedBookLogosProps) {
  const { isHere, place } = useBookAvailability();
  const visible = secondaryBooks.slice(0, maxVisible).map((book) => ({ book, away: notHere(place, isHere(book)) }));
  const overflow = secondaryBooks.length - visible.length;

  return (
    <div className="flex items-center shrink-0">
      <div className="relative" style={{ zIndex: visible.length + 2 }}>
        <WhaleWatchBookLogo book={bestBook} size="md" className={BOOK_LOGO_CLASS} />
      </div>
      {visible.map(({ book, away }, i) => (
        <div
          key={book}
          className={cn("-ml-1.5", away && NOT_BETTABLE_CLASS)}
          style={{ zIndex: visible.length - i + 1 }}
        >
          <WhaleWatchBookLogo
            book={book}
            size="sm"
            title={away ? notBettableIn(getBookDisplayName(book), away) : undefined}
            className={cn(BOOK_LOGO_CLASS, "ring-2 ring-surface-base")}
          />
        </div>
      ))}
      {overflow > 0 && (
        <div
          className="-ml-1 flex items-center justify-center shrink-0 rounded bg-surface-overlay ring-2 ring-surface-base data-num text-[9px] font-semibold text-zinc-400"
          style={{ width: 20, height: 20, zIndex: 0 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
});

export const ClassicStackedBookLogos = React.memo(function ClassicStackedBookLogos({
  bestBook,
  secondaryBooks,
  maxVisible = DEFAULT_MAX_VISIBLE,
}: StackedBookLogosProps) {
  const visible = secondaryBooks.slice(0, maxVisible);
  const overflow = secondaryBooks.length - visible.length;

  return (
    <div className="flex items-center shrink-0">
      <div className="relative" style={{ zIndex: visible.length + 2 }}>
        <WhaleWatchBookLogo book={bestBook} size="md" />
      </div>
      {visible.map((book, i) => (
        <div
          key={book}
          className="-ml-1.5"
          style={{ zIndex: visible.length - i + 1 }}
        >
          <WhaleWatchBookLogo
            book={book}
            size="sm"
            className="ring-2 ring-zinc-900/90 shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
          />
        </div>
      ))}
      {overflow > 0 && (
        <div
          className="-ml-1 flex items-center justify-center shrink-0 rounded bg-zinc-700/90 ring-2 ring-zinc-900/90 text-[9px] font-bold text-zinc-300 tabular-nums"
          style={{ width: 20, height: 20, zIndex: 0 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
});
